<script>
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import { companyImportApi } from '../stores/api.js';
  import { activeCompanyId, activeCompany } from '../stores/company.js';
  import { t, tImmediate } from '../i18n/index.js';
  // Il taglio delle richieste sta in un modulo senza Svelte: i limiti del
  // backend (200 file, 50 MB) vanno verificati, e da lì è testabile.
  import { planImportBatches } from '../lib/import-batches.js';
  import { Upload, FileText, CheckCircle, AlertTriangle, XCircle, Loader2, Trash2, RefreshCcw } from '../lib/icons/index.js';
  import { push } from 'svelte-spa-router';

  // See PaymentMethods.svelte for rationale — `embedded` lets Settings.svelte
  // mount this page as a tab body without doubling the Layout chrome.
  export let embedded = false;

  /** Files queued for upload, with per-file status. */
  let queue = []; // [{id, filename, size, content, status, result?, error?}]
  let dragOver = false;
  let busy = false;
  let summary = null; // {total, imported, refreshed, skipped, failed} aggregated across batches

  // Re-read files already in the archive instead of skipping them. Off by
  // default: the ordinary reason to upload the same file twice is having lost
  // track of what was already sent, and silently rewriting those invoices
  // would be the wrong answer to it.
  let refreshMetadata = false;
  let batchErr = '';


  let nextId = 1;

  function addFiles(fileList) {
    const newOnes = [];
    for (const f of fileList) {
      // Una fattura firmata si chiama «....xml.p7m»: il controllo sul .p7m va
      // fatto per primo, altrimenti il file finirebbe letto come testo e la
      // busta CAdES verrebbe distrutta dal browser prima ancora dell'upload.
      const isSigned = /\.p7m$/i.test(f.name);
      const isXml = /\.xml$/i.test(f.name) || f.type === 'text/xml' || f.type === 'application/xml';
      if (!isSigned && !isXml) continue; // silently skip the rest to keep the UI calm on folder drops
      newOnes.push({
        id: nextId++,
        filename: f.name,
        size: f.size,
        file: f,
        signed: isSigned,
        status: 'pending',
      });
    }
    queue = [...queue, ...newOnes];
  }

  function handleFileInput(e) {
    addFiles(e.target.files || []);
    e.target.value = ''; // allow picking the same file twice
  }

  function handleDrop(e) {
    e.preventDefault();
    dragOver = false;
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  }
  function handleDragOver(e) { e.preventDefault(); dragOver = true; }
  function handleDragLeave() { dragOver = false; }

  function removeOne(id) {
    queue = queue.filter(it => it.id !== id);
  }
  function clearAll() {
    if (busy) return;
    queue = [];
    summary = null;
    batchErr = '';
  }
  function clearDone() {
    queue = queue.filter(it => it.status === 'pending');
    summary = null;
  }

  async function readFileText(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ''));
      r.onerror = () => reject(r.error || new Error('read failed'));
      r.readAsText(file, 'utf-8');
    });
  }

  /**
   * I byte del file, senza passare da una stringa di testo.
   *
   * Serve per i .p7m: sono DER, e `readAsText` sostituirebbe ogni byte fuori
   * dall'ASCII con U+FFFD — la busta firmata arriverebbe al server già rotta.
   * Stesso approccio di SignInvoiceModal.svelte, che carica il .p7m firmato
   * fuori dal gestionale.
   */
  async function readFileBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const dataUrl = String(r.result || '');
        resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
      };
      r.onerror = () => reject(r.error || new Error('read failed'));
      r.readAsDataURL(file);
    });
  }

  async function startImport() {
    if (busy) return;
    const pending = queue.filter(it => it.status === 'pending');
    if (pending.length === 0) return;

    busy = true;
    batchErr = '';
    summary = summary || {
      total: 0, imported: 0, refreshed: 0, skipped: 0, failed: 0,
    };

    try {
      // Read file contents lazily, in chunks, to avoid pinning all XML in
      // memory when a user drops thousands of files. Le richieste sono tagliate
      // anche a byte, non solo a numero di file: un .p7m viaggia in base64 e
      // cinquanta file da 1 MB supererebbero il limite del corpo della
      // richiesta, facendo fallire in blocco l'intero gruppo.
      for (const slice of planImportBatches(pending)) {

        // Mark slice as uploading
        slice.forEach(it => { it.status = 'uploading'; });
        queue = [...queue];

        const payload = [];
        for (const it of slice) {
          try {
            if (it.signed) {
              const contentBase64 = await readFileBase64(it.file);
              payload.push({ filename: it.filename, contentBase64 });
            } else {
              const content = await readFileText(it.file);
              payload.push({ filename: it.filename, content });
            }
          } catch (err) {
            it.status = 'failed';
            it.error = `read error: ${err.message}`;
            summary.failed++;
            summary.total++;
          }
        }

        if (payload.length === 0) {
          queue = [...queue];
          continue;
        }

        let response;
        try {
          response = await companyImportApi.importBatch($activeCompanyId, payload, { refreshMetadata });
        } catch (err) {
          // Whole batch failed (network / 500). Mark all as failed.
          for (const it of slice) {
            if (it.status === 'uploading') {
              it.status = 'failed';
              it.error = err.message;
              summary.failed++;
              summary.total++;
            }
          }
          queue = [...queue];
          batchErr = err.message;
          continue;
        }

        // Map per-file results back to queue items by filename order.
        const resultByName = new Map();
        for (const r of response.results || []) {
          if (!resultByName.has(r.filename)) resultByName.set(r.filename, []);
          resultByName.get(r.filename).push(r);
        }
        for (const it of slice) {
          const arr = resultByName.get(it.filename);
          const r = arr?.shift();
          if (!r) {
            it.status = 'failed';
            it.error = 'no result returned';
            summary.failed++;
          } else {
            it.status = r.status;
            it.result = r;
            if (r.status === 'imported') summary.imported++;
            else if (r.status === 'refreshed') summary.refreshed++;
            else if (r.status === 'skipped') summary.skipped++;
            else summary.failed++;
          }
          summary.total++;
        }
        queue = [...queue];
      }
    } finally {
      busy = false;
    }
  }

  /**
   * Il motivo per cui un file è stato saltato o rifiutato, in italiano.
   *
   * Il backend risponde con un codice (`p7m_requires_base64`, `already_exists`,
   * …): senza questa traduzione l'utente leggerebbe il codice così com'è. Un
   * codice non ancora tradotto resta visibile invece di sparire.
   */
  function reasonLabel(reason) {
    if (!reason) return '';
    return tImmediate(`invoicesImport.reason.${reason}`, {}, reason);
  }

  function statusIcon(status) {
    return {
      pending: FileText,
      uploading: Loader2,
      imported: CheckCircle,
      refreshed: RefreshCcw,
      skipped: AlertTriangle,
      failed: XCircle,
    }[status] || FileText;
  }
  function statusColor(status) {
    return {
      pending: 'var(--color-text-muted)',
      uploading: 'var(--color-primary)',
      imported: 'var(--color-success)',
      refreshed: 'var(--color-success)',
      skipped: 'var(--color-warning)',
      failed: 'var(--color-danger)',
    }[status] || 'var(--color-text-muted)';
  }

  $: pendingCount = queue.filter(it => it.status === 'pending').length;
  $: hasResults = queue.some(it => ['imported', 'refreshed', 'skipped', 'failed'].includes(it.status));
</script>

{#snippet body()}
  <div class="max-w-5xl mx-auto">
    {#if !embedded}
      <div class="flex items-center gap-3 mb-2">
        <h1 class="section-title mb-0">{$t('invoicesImport.title')}</h1>
      </div>
    {/if}
    <p class="view-subtitle mt-2 mb-6">{$t('invoicesImport.subtitle')}</p>

    <!-- Discovery banner -->
    <div class="alert alert-accent mb-6">
      <AlertTriangle size={18} class="alert-mark mt-0.5" />
      <div class="text-xs space-y-2 measure" style="color: var(--color-text-body);">
        <p><strong>{$t('invoicesImport.help.whatTitle')}</strong> {$t('invoicesImport.help.whatBody')}</p>
        <p><strong>{$t('invoicesImport.help.signedTitle')}</strong> {$t('invoicesImport.help.signedBody')}</p>
        <p><strong>{$t('invoicesImport.help.dedupeTitle')}</strong> {$t('invoicesImport.help.dedupeBody')}</p>
        <p><strong>{$t('invoicesImport.help.noResendTitle')}</strong> {$t('invoicesImport.help.noResendBody')}</p>
      </div>
    </div>

    {#if !$activeCompany}
      <div class="card p-4 mb-4 text-sm font-mono" style="color: var(--color-text-muted);">
        {$t('invoicesImport.errors.noCompany')}
      </div>
    {:else}
      <!-- Drop zone -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        on:drop={handleDrop}
        on:dragover={handleDragOver}
        on:dragleave={handleDragLeave}
        class="card p-8 mb-4 border-w border-dashed text-center transition-colors {dragOver ? 'bg-primary-500/10' : ''}"
        style="border-color: {dragOver ? 'var(--color-primary)' : 'var(--color-tint-strong)'};"
      >
        <Upload size={36} class="mx-auto mb-3" style="color: var(--color-primary);" />
        <p class="text-sm font-bold mb-1" style="color: var(--color-text-heading);">{$t('invoicesImport.dropZone.title')}</p>
        <p class="text-xs font-mono mb-4" style="color: var(--color-text-muted);">{$t('invoicesImport.dropZone.subtitle')}</p>
        <label class="btn btn-primary btn-sm cursor-pointer">
          {$t('invoicesImport.dropZone.pick')}
          <input type="file" multiple accept=".xml,.p7m,text/xml,application/xml,application/pkcs7-mime" class="hidden" on:change={handleFileInput} />
        </label>
      </div>

      <label class="card p-4 mb-4 flex items-start gap-3 cursor-pointer focus-ring-host">
        <input
          type="checkbox"
          bind:checked={refreshMetadata}
          disabled={busy}
          class="mt-0.5 shrink-0"
        />
        <span class="text-xs measure" style="color: var(--color-text-body);">
          <strong class="block eyebrow mb-1" style="color: var(--color-text-heading);">
            {$t('invoicesImport.refresh.label')}
          </strong>
          {$t('invoicesImport.refresh.hint')}
        </span>
      </label>

      <ErrorBanner error={batchErr} />

      {#if summary}
        <div class="grid grid-cols-2 gap-3 mb-4 {refreshMetadata || summary.refreshed ? 'md:grid-cols-5' : 'md:grid-cols-4'}">
          <div class="card p-3">
            <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('invoicesImport.summary.total')}</span>
            <p class="text-xl font-bold font-mono" style="color: var(--color-text-heading);">{summary.total}</p>
          </div>
          <div class="card p-3">
            <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('invoicesImport.summary.imported')}</span>
            <p class="text-xl font-bold font-mono" style="color: var(--color-success);">{summary.imported}</p>
          </div>
          {#if refreshMetadata || summary.refreshed}
            <div class="card p-3">
              <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('invoicesImport.summary.refreshed')}</span>
              <p class="text-xl font-bold font-mono" style="color: var(--color-success);">{summary.refreshed}</p>
            </div>
          {/if}
          <div class="card p-3">
            <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('invoicesImport.summary.skipped')}</span>
            <p class="text-xl font-bold font-mono" style="color: var(--color-warning);">{summary.skipped}</p>
          </div>
          <div class="card p-3">
            <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('invoicesImport.summary.failed')}</span>
            <p class="text-xl font-bold font-mono" style="color: var(--color-danger);">{summary.failed}</p>
          </div>
        </div>
      {/if}

      {#if queue.length > 0}
        <div class="card p-4 mb-4">
          <div class="flex items-center justify-between mb-3">
            <h2 class="eyebrow eyebrow-lg" style="color: var(--color-primary);">
              {$t('invoicesImport.queue.title', { n: queue.length })}
            </h2>
            <div class="flex items-center gap-2">
              {#if hasResults}
                <button on:click={clearDone} disabled={busy}
                  class="flex items-center gap-1 px-2 py-1 eyebrow hover:text-action disabled:opacity-50"
                  style="color: var(--color-text-muted);">
                  <RefreshCcw size={12} /> {$t('invoicesImport.queue.clearDone')}
                </button>
              {/if}
              <button on:click={clearAll} disabled={busy}
                class="flex items-center gap-1 px-2 py-1 eyebrow hover:text-danger disabled:opacity-50"
                style="color: var(--color-text-muted);">
                <Trash2 size={12} /> {$t('invoicesImport.queue.clearAll')}
              </button>
            </div>
          </div>
          <div class="max-h-96 overflow-y-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b-w sticky top-0" style="border-color: var(--color-tint-medium); background-color: var(--color-surface-dark);">
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase w-8" style="color: var(--color-text-muted);"></th>
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase" style="color: var(--color-text-muted);">{$t('invoicesImport.queue.col.filename')}</th>
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase w-32" style="color: var(--color-text-muted);">{$t('invoicesImport.queue.col.invoice')}</th>
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase w-24" style="color: var(--color-text-muted);">{$t('invoicesImport.queue.col.direction')}</th>
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase w-32" style="color: var(--color-text-muted);">{$t('invoicesImport.queue.col.status')}</th>
                  <th class="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {#each queue as item (item.id)}
                  <tr class="border-b" style="border-color: var(--color-tint-soft);">
                    <td class="py-1 px-1">
                      <!-- svelte:component, not a bare `<Icon />` tag: the each
                           block is keyed by item.id, so a row is reused as its
                           status walks pending → uploading → imported. In
                           legacy (non-runes) mode a capitalised local tag is
                           resolved once per block creation, which would freeze
                           every row on the pending glyph. -->
                      <svelte:component this={statusIcon(item.status)} size={14} class={item.status === 'uploading' ? 'animate-spin' : ''} style="color: {statusColor(item.status)};" />
                    </td>
                    <td class="py-1 px-1 font-mono text-xs truncate max-w-xs" style="color: var(--color-text-body);">{item.filename}</td>
                    <td class="py-1 px-1 font-mono text-xs" style="color: var(--color-text-muted);">{item.result?.invoiceNumber || '-'}</td>
                    <td class="py-1 px-1 font-mono text-xs" style="color: var(--color-text-muted);">
                      {item.result?.direction ? $t(`invoicesImport.direction.${item.result.direction}`) : '-'}
                    </td>
                    <td class="py-1 px-1 font-mono text-xs" style="color: {statusColor(item.status)};">
                      {$t(`invoicesImport.statusLabel.${item.status}`)}
                      {#if item.error || item.result?.reason}
                        <div class="text-xs" style="color: var(--color-text-muted);">{item.error || reasonLabel(item.result?.reason)}</div>
                      {/if}
                    </td>
                    <td class="py-1 px-1">
                      {#if item.result?.invoiceId}
                        <button on:click={() => push(`/invoices/${item.result.invoiceId}`)}
                          class="text-xs font-bold uppercase hover:text-action"
                          style="color: var(--color-text-muted);">→</button>
                      {:else if item.status === 'pending'}
                        <button on:click={() => removeOne(item.id)} disabled={busy}
                          class="hover:text-danger disabled:opacity-30"
                          style="color: var(--color-text-muted);">
                          <Trash2 size={12} />
                        </button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      {/if}

      <div class="flex items-center justify-end gap-3">
        {#if pendingCount > 0}
          <span class="text-xs font-mono" style="color: var(--color-text-muted);">{$t('invoicesImport.pendingHint', { n: pendingCount })}</span>
        {/if}
        <button on:click={startImport} disabled={busy || pendingCount === 0}
          class="btn btn-primary gap-2">
          {#if busy}
            <Loader2 size={18} class="animate-spin" />
            <span>{$t('invoicesImport.importing')}</span>
          {:else}
            <Upload size={18} />
            <span>{$t('invoicesImport.startImport', { n: pendingCount })}</span>
          {/if}
        </button>
      </div>
    {/if}
  </div>
{/snippet}

{#if embedded}
  {@render body()}
{:else}
  <Layout>
    {@render body()}
  </Layout>
{/if}
